import { useCallback, useEffect, useState } from "react";
import { aiAgentService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { ScrollText, Loader2, Play, Zap, CheckCircle2, XCircle, Clock } from "lucide-react";
import {
  AgentIcon, RiskBadge, useTenantScope, TenantPicker, SelectTenantNotice, PageHeader, fmtDate,
} from "./shared";

const ACTION_STATUS_STYLE = {
  executed: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
  approved: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
  pending_approval: "text-amber-600 bg-amber-500/10 border-amber-500/30",
  proposed: "text-muted-foreground bg-muted/40 border-border",
  rejected: "text-red-600 bg-red-500/10 border-red-500/30",
  cancelled: "text-red-600 bg-red-500/10 border-red-500/30",
};

function RunRow({ run }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4 flex items-start gap-3" data-testid={`run-row-${run.id}`}>
      <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <AgentIcon code={run.agentCode} className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-bold text-foreground">{run.agentCode} agent run</h3>
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-border text-muted-foreground capitalize">
            <Play className="h-3 w-3" /> {run.trigger}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">{run.summary || "—"}</p>
        <p className="text-[11px] text-muted-foreground/70 mt-1">
          {fmtDate(run.startedAt)} · {run.recommendationsGenerated} recommendation(s) · status {run.status}
        </p>
      </div>
    </div>
  );
}

function ActionRow({ a }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4 flex items-start gap-3" data-testid={`action-row-${a.id}`}>
      <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <AgentIcon code={a.agentCode} className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-bold text-foreground">{a.title}</h3>
          <div className="flex items-center gap-1.5">
            {a.isExternalSend && a.simulated && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-600">SIMULATED</span>}
            <RiskBadge level={a.riskLevel} />
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border capitalize ${ACTION_STATUS_STYLE[a.status] || ACTION_STATUS_STYLE.proposed}`}>{(a.status || "").replace("_", " ")}</span>
          </div>
        </div>
        {a.description && <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{a.description}</p>}
        {a.executionResult && <p className="text-[11px] text-muted-foreground/80 mt-1.5 bg-muted/40 rounded-lg p-2">{a.executionResult}</p>}
        <p className="text-[11px] text-muted-foreground/70 mt-1">{a.executedAt ? `Executed ${fmtDate(a.executedAt)}` : `Created ${fmtDate(a.createdOn)}`}</p>
      </div>
    </div>
  );
}

export default function AgentAuditPage() {
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [tab, setTab] = useState("runs");
  const [runs, setRuns] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);

  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setRuns([]); setActions([]); return; }
    setLoading(true);
    Promise.all([aiAgentService.runs({ ...params, size: 100 }), aiAgentService.actions({ ...params, size: 100 })])
      .then(([r, a]) => { setRuns(r.items || []); setActions(a.items || []); })
      .catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [blocked, params]);

  useEffect(() => { load(); }, [load]);

  const list = tab === "runs" ? runs : actions;
  const executed = actions.filter((a) => a.status === "executed").length;

  return (
    <div data-testid="agent-audit-page">
      <PageHeader icon={ScrollText} title="Agent Audit"
        subtitle="An immutable trail of every agent run and the actions taken on your behalf.">
        <TenantPicker {...scope} />
      </PageHeader>

      {blocked ? <SelectTenantNotice /> : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="bg-card border border-border rounded-xl p-4" data-testid="audit-stat-runs"><div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Total runs</span><Play className="h-4 w-4 text-muted-foreground" /></div><p className="text-2xl font-bold text-foreground mt-2">{runs.length}</p></div>
            <div className="bg-card border border-border rounded-xl p-4" data-testid="audit-stat-actions"><div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Actions</span><Zap className="h-4 w-4 text-muted-foreground" /></div><p className="text-2xl font-bold text-foreground mt-2">{actions.length}</p></div>
            <div className="bg-card border border-border rounded-xl p-4" data-testid="audit-stat-executed"><div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Executed</span><CheckCircle2 className="h-4 w-4 text-muted-foreground" /></div><p className="text-2xl font-bold text-foreground mt-2">{executed}</p></div>
          </div>

          <div className="flex bg-muted/40 rounded-lg p-1 mb-4 w-fit" data-testid="audit-tabs">
            {[{ k: "runs", l: "Agent Runs", i: Clock }, { k: "actions", l: "Actions", i: Zap }].map((t) => (
              <button key={t.k} onClick={() => setTab(t.k)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${tab === t.k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`audit-tab-${t.k}`}><t.i className="h-3.5 w-3.5" /> {t.l}</button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : list.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="audit-empty">
              <XCircle className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No {tab} yet.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="audit-list">
              {tab === "runs" ? runs.map((r) => <RunRow key={r.id} run={r} />) : actions.map((a) => <ActionRow key={a.id} a={a} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
